import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Fade,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import { searchEmployeeByCPF } from '../../services/api';
import toast from 'react-hot-toast';

interface SearchFormProps {
  onEmployeeFound: (data: any) => void;
}

const SearchForm: React.FC<SearchFormProps> = ({ onEmployeeFound }) => {
  const [cpf, setCpf] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0,3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0,3)}.${numbers.slice(3,6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0,3)}.${numbers.slice(3,6)}.${numbers.slice(6,9)}-${numbers.slice(9,11)}`;
  };

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setCpf(formatted);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const cleanedCPF = cpf.replace(/\D/g, '');
    if (cleanedCPF.length !== 11) {
      setError('CPF inválido. Digite 11 números.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      toast.loading('Consultando banco de dados...', { id: 'search' });
      const employee = await searchEmployeeByCPF(cleanedCPF);
      toast.success('Consulta realizada com sucesso!', { id: 'search' });
      onEmployeeFound(employee);
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'CPF não consta nesta lista da Ação do 1/3 de férias. Entre em contato com a sua regional.';
      setError(errorMsg);
      toast.error(errorMsg, { id: 'search' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in={true} timeout={500}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 3, md: 4 }, 
          maxWidth: 500, 
          mx: 'auto',
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid',
          borderColor: 'primary.light',
          boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <PersonIcon 
            sx={{ 
              fontSize: 60, 
              color: 'primary.main',
              mb: 2,
              p: 1,
              backgroundColor: 'primary.light',
              borderRadius: '50%',
            }} 
          />
          <Typography 
            variant="h5" 
            gutterBottom 
            color="primary.main"
            fontWeight="bold"
          >
            Consulta de Valores
          </Typography>
          
          <Typography variant="body2" color="text.secondary">
            Digite seu CPF para consultar os valores devidos (Ação do 1/3 de Férias)
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Digite seu CPF"
            value={cpf}
            onChange={handleCPFChange}
            placeholder="000.000.000-00"
            variant="outlined"
            margin="normal"
            required
            disabled={loading}
            inputProps={{ maxLength: 14 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              sx: { 
                borderRadius: 2,
                backgroundColor: 'white',
              }
            }}
            helperText="Apenas números, o sistema formatará automaticamente"
          />

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2, 
                borderRadius: 2,
                animation: 'fadeIn 0.3s ease-out'
              }}
            >
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || cpf.replace(/\D/g, '').length !== 11}
            sx={{ 
              mt: 3, 
              py: 1.5,
              borderRadius: 2,
              fontWeight: 'bold',
              fontSize: '1.1rem',
              position: 'relative',
              overflow: 'hidden',
              '&:after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                transition: '0.5s',
              },
              '&:hover:after': {
                left: '100%',
              }
            }}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Consultando...' : 'Consultar Valores'}
          </Button>

          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              display: 'block', 
              textAlign: 'center', 
              mt: 2 
            }}
          >
            Seus dados estão protegidos e serão usados apenas para esta consulta
          </Typography>
        </form>
      </Paper>
    </Fade>
  );
};

export default SearchForm;